import { CartItem } from '../interfaces/cart-item';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject, throwError, timer } from 'rxjs';
import { catchError, map, skip, startWith, take } from 'rxjs/operators';
import { CountryInfo, IDBProduct, ProductOption } from '../interfaces/product';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { UserService } from './user.service';
import { StorageService } from './storage.service';
import { OffcanvasCartService } from './offcanvas-cart.service';
import { PromotionService } from '../api/promotion.service';
import { AnalyticsService } from './analytics.service';
import { Injectable } from '@angular/core';
import { CurrencyService, LocalBasket } from './currency.service';
import { CultureService } from '../culture/culture.service';

interface CartTotal {
    title: string;
    price: number;
    type: 'shipping'|'fee'|'tax'|'other'|'discount';
    /// Delivery before the free-delivery offer - set only when the offer took something off `price`.
    before?: number;
    /// What the offer is, for the saving's line.
    note?: string;
}

/// One line under the subtotal as the pages show it, already in the visitor's currency.
export interface CartRow {
    title: string;
    amount: number;
    type: 'shipping'|'fee'|'tax'|'other'|'discount'|'offer';
    note?: string;
}

/// The basket as shown, plus its lines under the subtotal: delivery at its full price and the free-delivery offer as a
/// saving of its own, so a buyer can see the shop paying part of their delivery (it used to show only the reduced figure).
export interface LocalCart extends LocalBasket {
    rows: CartRow[];
}

interface CartData {
    items: CartItem[];
    quantity: number;
    subtotal: number;
    totals: CartTotal[];
    total: number;
}

@Injectable({
    providedIn: 'root'
})
export class CartService {
    private data: CartData = {
        items: [],
        quantity: null,
        subtotal: 0,
        totals: [],
        total: 0
    }; 

    public countryInfo:CountryInfo; 
    
    private itemsSubject$: BehaviorSubject<CartItem[]> = new BehaviorSubject(this.data.items);
    private quantitySubject$: BehaviorSubject<number> = new BehaviorSubject(this.data.quantity);
    private subtotalSubject$: BehaviorSubject<number> = new BehaviorSubject(this.data.subtotal);
    private totalsSubject$: BehaviorSubject<CartTotal[]> = new BehaviorSubject(this.data.totals);
    private totalSubject$: BehaviorSubject<number> = new BehaviorSubject(this.data.total);
    private onAddingSubject$: Subject<IDBProduct> = new Subject();
    private onQuantityReached$: Subject<any> = new Subject();
    public onQuickView$: Subject<any> = new Subject();
    private _cartLoaded = new BehaviorSubject<boolean>(false);

    // ---- delivery. What each line in the basket costs to deliver, per unit, as the server
    // says for the country in play: the delivery address's once the checkout has one, else the
    // country typed at the guest checkout, else the visitor's. The Shipping line is these times
    // the quantities - it used to be a hard-coded 0 - and the checkout charges the same figure.
    // By product AND size (shippingKey), as the quote's lines come: what a line is charged can
    // rest on its size's own offer for the country (a warehouse's, a fee partly carried by the price).
    private shippingPerUnit: { [line: string]: number } = {};
    /// Each line's charge as quoted, with the quantity it was quoted for: a seller on PrimeHat's couriers is ONE parcel charge
    /// shared by its lines (not per item), so the quote's amount is used while the quantity is still the one quoted.
    private shippingAmount: { [line: string]: { quantity: number; amount: number } } = {};
    private shippingAsked = '';
    private shippingAddressId = 0;
    private shippingCountry = '';
    /// In Bangladesh, the upazila or thana of the address being quoted for (a seller's Dhaka city or outside fee).
    private shippingUpazila = 0;
    private shippingTimer: any = null;
    public cartLoaded$ = this._cartLoaded.asObservable();

    // The first read of the basket - the server's copy, for a signed-in shopper - has come back, for
    // everyone (cartLoaded$ fires for guests only). A page that replaces the basket, like the Meta
    // checkout link, waits for it: that late answer would otherwise put the old basket back.
    private readonly firstRead$ = new ReplaySubject<void>(1);
    whenRead(): Observable<void> { return this.firstRead$.pipe(take(1)); }

    get items(): ReadonlyArray<CartItem> {
        return this.data.items;
    }

    get quantity(): number {
        return this.data.quantity;
    }

    readonly items$: Observable<CartItem[]> = this.itemsSubject$.asObservable();
    readonly quantity$: Observable<number> = this.quantitySubject$.asObservable();
    readonly subtotal$: Observable<number> = this.subtotalSubject$.asObservable();
    readonly totals$: Observable<CartTotal[]> = this.totalsSubject$.asObservable();
    readonly total$: Observable<number> = this.totalSubject$.asObservable(); 

    /// The basket in the currency the visitor is shown, added up from what is shown (CurrencyService.basket): the page
    /// writes its lines, subtotal and total from here, so they add up to the cent whatever the rate. Checkout charges total$.
    readonly local$: Observable<LocalCart> = combineLatest([
        this.itemsSubject$, this.totalsSubject$, this.currency.changes$.pipe(startWith(null)),
    ]).pipe(map(([items, totals]) => {
        const basket = this.currency.basket(
            (items || []).map(i => ({ price: i.option ? i.option.Price : 0, quantity: i.quantity })), totals || []);
        const rows: CartRow[] = [];
        (totals || []).forEach((t, i) => {
            if (basket.before[i] != null) {
                rows.push({ title: this.culture.t('shipping-charge', 'Shipping'), amount: basket.before[i], type: t.type });
                rows.push({ title: this.culture.t('free-delivery-offer', 'Free delivery offer'), amount: basket.savings[i], type: 'offer', note: t.note });
            } else {
                rows.push({ title: t.title, amount: basket.extras[i], type: t.type });
            }
        });
        return { ...basket, rows };
    }));
    readonly onLimit$: Observable<any> = this.onQuantityReached$.asObservable();  
    readonly onAdding$: Observable<any> = this.onAddingSubject$.asObservable();
    
    public onQuickViewObservable$ = this.onQuickView$.asObservable();
    baseUrl:string = environment.apiUrl;
    
    constructor( 
        private http: HttpClient,
        private storage:StorageService,
        private dropCart:OffcanvasCartService,
        private userService:UserService,
        private analytics:AnalyticsService,
        private promotion:PromotionService,
        private currency:CurrencyService,
        private culture:CultureService)
    {
        // The welcome deal arrives after the cart does, and changes with who is signed in.
        this.promotion.welcomeDeal$.pipe(skip(1)).subscribe(() => this.calc());
        this.promotion.freeShipping$.pipe(skip(1)).subscribe(() => this.calc());
    }

    init()
    { 
        // Reaching checkout, or finishing an order, can change whether the buyer qualifies.
        this.promotion.refresh();
        this.load();
        this.calc();
    }
 
    add(product: IDBProduct, quantity: number, option:ProductOption, isQuick:boolean = false): Observable<any> 
    {      
        if(this.userService.isLoggedIn() == true)
        {
            return this.addOrUpdateCartItem(product.ID, quantity, option).pipe(map((res:any) => 
            {    
                this.onQuantityReached$.next(res);
                this.onAddingSubject$.next(res);
                if(res && res.ID > 0)
                {
                    // Here rather than in the button, so only an add the server accepted is
                    // reported. (The product page and quick view are the live callers; the card
                    // and wishlist add buttons are commented out.)
                    this.analytics.addToCart(product, option, quantity);

                    let item = this.items.find(item => item.product.ID === product.ID && item.option.ID === option.ID)

                    if (item) 
                    {
                        item.quantity += quantity;
                    } 
                    else 
                    {
                        item = {product, quantity, option};
                        this.data.items.push(item);
                    }
        
                    this.calc(); 
                    this.dropCart.open(); 
                    if(isQuick === true)
                    {
                        this.onQuickView$.next(true);
                    }
                    return item;
                } 
                if(res && res.MaxExceed == true){

                }
            }));
        }
        else
        {
            return timer(500).pipe(map(() => {
                this.onAddingSubject$.next(product);
                this.analytics.addToCart(product, option, quantity);

                let item = this.items.find(item => item.product.ID === product.ID && item.option.ID === option.ID)

                if (item) 
                {
                    item.quantity += quantity;
                } 
                else 
                {
                    item = {product, quantity, option};
                    this.data.items.push(item);
                }
    
                this.calc(); 
                this.save();
                this.dropCart.open();
                 if(isQuick === true){
                        this.onQuickView$.next(true);
                    }
                return item;
            })); 
        } 
    }

    update(updates: { item: CartItem, quantity: number, action:string }[]): Observable<any> { 
        if (!updates?.length) {
            return of('minimum');
        }
    
        var firstUpdate = updates[0]; 
        return this.addOrUpdateCartItem(firstUpdate.item.product.ID, firstUpdate.quantity, firstUpdate.item.option).pipe(
            map(res => {
                if(res && res.MaxExceed == true){
                     
                }
                else if(res && res.ID > 0){
                    updates.forEach(update => {
                        const item = this.items.find(eachItem => eachItem === update.item);
                        if (item) {
                            // A quantity change is reported as the difference, which is what
                            // GA4 means by an add or a remove - read before it is overwritten.
                            const change = update.quantity - item.quantity;
                            if (change > 0) {
                                this.analytics.addToCart(item.product, item.option, change);
                            } else if (change < 0) {
                                this.analytics.removeFromCart(item.product, item.option, -change);
                            }

                            item.quantity = update.quantity;
                        }
                    });
                }
                else{ 
                } 
                this.calc();
                return res; 
            }),
            catchError(err => {
                console.error('Cart update failed:', err);
                return throwError(() => err);
            })
        );
    }
    

    remove(item: CartItem): Observable<void> 
    {     
        if(this.userService.isLoggedIn() == true)
        {
            return this.removeFromCart(item.product.ID, item.option.ID).pipe(map((res) =>
            {
                if(res == true)
                {
                    this.analytics.removeFromCart(item.product, item.option, item.quantity);
                    this.data.items = this.data.items.filter(eachItem => eachItem !== item);
                    this.calc();
                }
            }));
        }
        else
        {
            return timer(500).pipe(map(() =>
            {
                this.analytics.removeFromCart(item.product, item.option, item.quantity);
                this.data.items = this.data.items.filter(eachItem => eachItem !== item);
                    this.calc();
                    this.save();
            }));
        } 
    }

    private calc(): void 
    {
        let quantity = 0;
        let subtotal = 0;

        this.data.items.forEach(item => 
        {
            quantity += item.quantity;
            subtotal += item.option.Price * item.quantity;
        });

        const totals: CartTotal[] = [];

        let shipping = 0;
        this.data.items.forEach(item =>
        {
            const key = CartService.shippingKey(item.product.ID, item.option && item.option.ID);
            const quoted = this.shippingAmount[key];
            shipping += quoted && quoted.quantity === item.quantity ? quoted.amount : (this.shippingPerUnit[key] || 0) * item.quantity;
        });

        // Free delivery over $200, up to $25 of it, worked out as the server works it out when the
        // payment is opened (FreeShippingOffer): on the items' total before any discount.
        const offer = this.promotion.freeShipping;
        const offered = shipping > 0 && PromotionService.qualifiesForFreeShipping(offer, subtotal);
        const before = Math.round(shipping * 100) / 100;
        shipping = PromotionService.deliveryAfterOffer(offer, subtotal, shipping);

        // `price` is what is charged for delivery (every sum uses it); `before` lets the pages show the full delivery and
        // the offer's saving as two lines (local$ rows).
        totals.push({
            title: offered
                ? this.culture.t('shipping-with-offer', 'Shipping ({amount}+ offer)', { amount: this.currency.written(this.currency.convert(offer.Over)) })
                : this.culture.t('shipping-charge', 'Shipping'),
            price: Math.round(shipping * 100) / 100,
            type: 'shipping',
            before: offered && before > shipping ? before : undefined,
            note: offered
                ? this.culture.t('free-delivery-offer-note', 'Orders of {over} or more: we pay up to {covers} of delivery', {
                    over: this.currency.written(this.currency.convert(offer.Over)),
                    covers: this.currency.written(this.currency.convert(offer.Covers)) })
                : undefined,
        });
        // totals.push({
        //     title: 'Tax',
        //     price: subtotal * 0.20,
        //     type: 'tax'
        // });

        // The first-order welcome deal, worked out as the server will work it out when the
        // payment is opened, so the total shown is the total charged.
        const welcome = PromotionService.discountFor(this.promotion.welcomeDeal, subtotal);
        if (welcome > 0)
        {
            totals.push({
                title: this.culture.t('welcome-deal', 'Welcome deal'),
                price: -welcome,
                type: 'discount'
            });
        }

        const total = subtotal + totals.reduce((acc, eachTotal) => acc + eachTotal.price, 0);

        this.data.quantity = quantity;
        this.data.subtotal = subtotal;
        this.data.totals = totals;
        this.data.total = total; 
        this.itemsSubject$.next(this.data.items);
        this.quantitySubject$.next(this.data.quantity);
        this.subtotalSubject$.next(this.data.subtotal);
        this.totalsSubject$.next(this.data.totals);
        this.totalSubject$.next(this.data.total);
        this.refreshShipping();
        
        if(!this.userService.isLoggedIn()){
            this._cartLoaded.next(true);
        }
    }
 
    /// One basket line as the delivery quote names it: the product and the size, as they were asked about.
    private static shippingKey(productId: number, optionId: number): string
    {
        return (Number(productId) || 0) + ':' + (Number(optionId) || 0);
    }

    /// The delivery address the checkout has settled on: delivery is quoted for ITS country from then on.
    public setShippingAddress(addressId: number, bdUpazilaId: number = 0): void
    {
        const id = Number(addressId) || 0;
        const upazila = Number(bdUpazilaId) || 0;
        if (id === this.shippingAddressId && upazila === this.shippingUpazila) { return; }
        this.shippingAddressId = id;
        // The server reads a saved address's own area; this only asks again when it changed.
        this.shippingUpazila = upazila;
        this.refreshShipping();
    }

    /// The upazila or thana picked at the guest checkout, in Bangladesh.
    public setShippingBdPlace(upazilaId: number): void
    {
        const upazila = Number(upazilaId) || 0;
        if (upazila === this.shippingUpazila) { return; }
        this.shippingUpazila = upazila;
        this.refreshShipping();
    }

    /// The country typed at the guest checkout, two letters.
    public setShippingCountry(code: string): void
    {
        const country = /^[A-Za-z]{2}$/.test(code || '') ? code.toUpperCase() : '';
        if (country === this.shippingCountry) { return; }
        this.shippingCountry = country;
        this.refreshShipping();
    }

    /// Asks the server what the basket's delivery comes to, when what it would be asked has
    /// changed. Browser only; briefly held back, because a quantity stepper fires in bursts.
    private refreshShipping(): void
    {
        if (typeof window === 'undefined') { return; }

        const items = this.data.items
            .filter(i => i && i.product && i.product.ID > 0 && i.quantity > 0)
            .map(i => ({ ProductId: i.product.ID, OptionId: (i.option && i.option.ID) || 0, Quantity: i.quantity }));
        const asked = this.shippingAddressId + '|' + this.shippingCountry + '|' + this.shippingUpazila + '|' + items.map(i => i.ProductId + ':' + i.OptionId + 'x' + i.Quantity).sort().join(',');
        if (asked === this.shippingAsked) { return; }
        this.shippingAsked = asked;

        if (this.shippingTimer) { clearTimeout(this.shippingTimer); }
        if (items.length === 0) { this.shippingPerUnit = {}; this.shippingAmount = {}; return; }

        this.shippingTimer = setTimeout(() =>
        {
            this.http.post<any>(this.baseUrl + '/api/Eligibility/CartShipping',
                { AddressId: this.shippingAddressId, Country: this.shippingCountry || null, BdUpazilaId: this.shippingUpazila || null, Items: items })
            .subscribe(quote =>
            {
                if (!quote || quote.Failed || asked !== this.shippingAsked) { return; }

                const perUnit: { [line: string]: number } = {};
                (quote.Lines || []).forEach((l: any) => perUnit[CartService.shippingKey(l.ProductId, l.OptionId)] = Number(l.PerUnit) || 0);
                this.shippingPerUnit = perUnit;
                const amounts: { [line: string]: { quantity: number; amount: number } } = {};
                (quote.Lines || []).forEach((l: any) => amounts[CartService.shippingKey(l.ProductId, l.OptionId)] = { quantity: Number(l.Quantity) || 0, amount: Number(l.Amount) || 0 });
                this.shippingAmount = amounts;

                // The lines' PRICES for that country too. A price can depend on the country - a
                // warehouse's, or the small-fee surcharge - and the checkout charges by the
                // delivery address's, while the basket was filled from wherever the shopper was
                // browsing. On a copy of the option: the product page may be showing the same object.
                (quote.Lines || []).forEach((l: any) =>
                {
                    if (l.UnitPrice === null || l.UnitPrice === undefined || !(l.OptionId > 0)) { return; }

                    this.data.items
                        .filter(i => i.product && i.option && i.product.ID === l.ProductId && i.option.ID === l.OptionId && i.option.Price !== l.UnitPrice)
                        .forEach(i => i.option = Object.assign({}, i.option, {
                            Price: l.UnitPrice,
                            RegularPrice: l.RegularPrice !== null && l.RegularPrice !== undefined ? l.RegularPrice : i.option.RegularPrice,
                            Discount: l.Discount !== null && l.Discount !== undefined ? l.Discount : i.option.Discount,
                        }));
                });

                this.calc();
            }, () => { this.shippingAsked = ''; /* asked again at the next change */ });
        }, 250);
    }

    public load(): void 
    {  
        const items = this.storage.getItem('cartItems'); 
        var idstr = ""; 
        var model:any[] = [];

        try
        {
            if (items) 
            {
                var jitems = JSON.parse(items) as CartItem[]; 
                for(var i = 0; i < jitems.length; i++)
                { 
                    model.push({ID:jitems[i].product.ID, OptionId:jitems[i].option.ID, Quantity:jitems[i].quantity})
                }
            }   
    
            this.getCartItemsFromDb(model).subscribe(res =>
            {
                if(res && res.length > 0)
                {
                    this.data.items = res; 
                    this.calc();
                    this.storage.removeItem('cartItems')
                }
                this.firstRead$.next();
            }, () => this.firstRead$.next());
        }
        catch { this.firstRead$.next(); } 

        if (items) 
        {
            this.data.items = JSON.parse(items);
            
        }
        else{
            this.data.items = []; 
            this.calc(); 
            this.save();
        }
    }
    public save(): void 
    {
        this.storage.setItem('cartItems', JSON.stringify(this.items));
    } 

    getCartItemsFromDb(model:any[]): Observable<CartItem[]> 
    {   
        return this.http.post<CartItem[]>(this.baseUrl +`/api/UserProductItem/GetAllCartItems`, model);
    }

    addOrUpdateCartItem(prodId:number, quantity:number, option:ProductOption): Observable<any> 
    { 
        const params:{[param:string]:string} = {};  

        return this.http.get<CartItem[]>(this.baseUrl +`/api/UserProductItem/AddToCart/${prodId}/${option.ID}/${quantity}`, {params}); 
    }

    removeFromCart(prodId:number, optionId:number): Observable<boolean> 
    { 
        const params:{[param:string]:string} = {};  
        return this.http.get<boolean>(this.baseUrl +`/api/UserProductItem/RemoveFromCart/${prodId}/${optionId}`, {params}); 
    }

    placeOrder(sessionId:string): Observable<any> 
    {  
        return this.http.post<any>(this.baseUrl +`/api/Order/CreateOrder/${sessionId}`, {}); 
    }

    getAllUserOrder(model:any): Observable<any> 
    {  
        return this.http.post<any>(this.baseUrl +`/api/Order/GetAllUserOrder/`, {}); 
    }

    getAllNijerOrder(model:any): Observable<any> 
    {  
        return this.http.post<any>(this.baseUrl +`/api/Publisher/GetAllNijerOrder/`, {}); 
    }

    processSingleOrderRequest(model:any): Observable<any> 
    {    
        return this.http.post<any>(this.baseUrl +`/api/Publisher/ProcessSingleOrderRequest/`, model);   
    }

    processOder(model:any): Observable<any> 
    {  
        return this.http.post<any>(this.baseUrl +`/api/Publisher/ProcessOrderRequest/`, model); 
    }

    getAllShopOrder(model:any): Observable<any> 
    {  
        return this.http.post<any>(this.baseUrl +`/api/Order/GetAllShopOrder/`, {}); 
    }

    getOrder(orderId:any, action = 're'): Observable<any>
    {
        return this.http.get<any>(this.baseUrl +`/api/Order/GetOrder?action=${action}&id=${orderId}`);
    }

    /// Google Customer Reviews' survey opt-in for the buyer's own order, or null where none is offered.
    getReviewOptIn(orderRef: string): Observable<any>
    {
        return this.http.get<any>(this.baseUrl + `/api/Order/ReviewOptIn?id=${encodeURIComponent(orderRef)}`);
    }

    /// Track Order: the order number AND the email it was placed with (Order/Track) - a number alone answers nothing.
    trackOrder(orderRef: string): Observable<any>
    {
        return this.http.post<any>(this.baseUrl + '/api/Order/Track', { OrderRef: orderRef });
    }
}
